import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';

// ── The marketing agents ───────────────────────────────────────────────────
//
// Four agents live in server/agents/marketing/. This is where Julian runs a
// campaign, reads the posts they wrote, copies the approved ones into
// Facebook by hand, and writes down what came back. That last part is the
// whole point: the agents judge a post by the jobs and profit it produced,
// and they can only do that if every lead is tied to the post it came from.
//
// TEST MODE is permanent on this screen. There is no switch, because there is
// no switch on the server either.

const SERVICES = ['drywall repair', 'painting', 'doors', 'finish carpentry', 'general handyman repairs'];
const COUNTS = [
  ['responses', 'Replies'], ['dms', 'DMs'], ['leads', 'Leads'], ['qualified', 'Qualified'],
  ['appointments', 'Visits'], ['estimates', 'Estimates'], ['customers', 'Customers'],
  ['revenue', 'Revenue $'], ['cost', 'Job cost $'],
];
const VERDICT = { scale: 'Scale it', continue: 'Keep running', modify: 'Change it', stop: 'Stop it', keep_testing: 'Keep testing' };
const money = (n) => `$${Math.round(Number(n) || 0).toLocaleString()}`;

export default function Marketing() {
  const { authHeaders, showToast, requests } = useApp();
  const [status, setStatus] = useState(null);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(null);         // { campaign, results }
  const [busy, setBusy] = useState(false);
  const [services, setServices] = useState(SERVICES);
  const [useModel, setUseModel] = useState(false);
  const [drafts, setDrafts] = useState({});       // placementId -> edits
  const [dm, setDm] = useState({ message: '', photos: 0 });
  const [read, setRead] = useState(null);

  const fail = (err) => showToast(err.response?.data?.message || err.message);

  const loadList = async () => {
    try { setList((await axios.get('/api/marketing/campaigns', authHeaders())).data); } catch (err) { fail(err); }
  };
  const openCampaign = async (id) => {
    try { setOpen((await axios.get(`/api/marketing/campaigns/${id}`, authHeaders())).data); setDrafts({}); } catch (err) { fail(err); }
  };

  useEffect(() => {
    axios.get('/api/marketing/status', authHeaders()).then((r) => setStatus(r.data)).catch(() => {});
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post('/api/marketing/campaigns', {
        name: `Test campaign ${new Date().toLocaleDateString()}`,
        geography: ['Utah County', 'Salt Lake County'],
        services, useModel,
      }, authHeaders());
      setOpen(res.data); setDrafts({});
      loadList();
    } catch (err) { fail(err); }
    setBusy(false);
  };

  const approve = async (v) => {
    let ownerApproved = false;
    if (v.status === 'needs_owner') {
      // Said out loud, because this is the one place an owner-only promise
      // gets through.
      if (!window.confirm(`${v.reviewNote}\n\nApprove this as the owner?`)) return;
      ownerApproved = true;
    }
    try {
      const res = await axios.post(`/api/marketing/campaigns/${open.campaign._id}/variants/${encodeURIComponent(v.id)}/approve`, { ownerApproved }, authHeaders());
      setOpen(res.data);
    } catch (err) { fail(err); }
  };

  const savePlacement = async (p) => {
    const patch = drafts[p.id];
    if (!patch) return;
    try {
      const res = await axios.patch(`/api/marketing/campaigns/${open.campaign._id}/placements/${encodeURIComponent(p.id)}`, patch, authHeaders());
      setOpen(res.data);
      setDrafts((d) => { const n = { ...d }; delete n[p.id]; return n; });
      showToast('Saved');
    } catch (err) { fail(err); }
  };

  const rejected = async (p) => {
    const reason = window.prompt('What did the group or Facebook say?');
    if (reason === null) return;
    try {
      const res = await axios.post(`/api/marketing/campaigns/${open.campaign._id}/placements/${encodeURIComponent(p.id)}/rejected`, { reason }, authHeaders());
      setOpen(res.data);
      window.alert(res.data.advice.action === 'stop_and_notify_owner'
        ? `STOP. ${res.data.advice.reason}` : res.data.advice.reason);
    } catch (err) { fail(err); }
  };

  const link = async (p, requestId) => {
    if (!requestId) return;
    try {
      await axios.post('/api/marketing/attribute', { requestId, campaignId: open.campaign._id, placementId: p.id }, authHeaders());
      await openCampaign(open.campaign._id);
      showToast('Lead linked to this post');
    } catch (err) { fail(err); }
  };

  const qualify = async () => {
    try { setRead((await axios.post('/api/marketing/qualify', dm, authHeaders())).data); } catch (err) { fail(err); }
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => showToast('Copied. Paste it into the group yourself.')).catch(() => {});
  };

  const edit = (p, field, value) => setDrafts((d) => ({ ...d, [p.id]: { ...(d[p.id] || {}), [field]: value } }));
  const val = (p, field) => (drafts[p.id] && field in drafts[p.id] ? drafts[p.id][field] : p[field] ?? '');

  const c = open?.campaign;
  const r = open?.results;
  const byPlacement = Object.fromEntries((r?.placements || []).map((p) => [p.id, p]));
  const verdicts = Object.fromEntries((r?.director?.rows || []).map((row) => [row.variantId, row]));

  return (
    <div className="mk">
      <div className="alert mk-test">
        <strong>TEST MODE.</strong> Nothing here posts, sends a DM or a text, or spends money. You copy an approved post
        and put it in the group yourself, then log what came back.
      </div>

      {/* ── Agent 2 on one lead ───────────────────────────────────────── */}
      <div className="card mk-card">
        <div className="mk-h">Check a lead</div>
        <div className="mk-sub">Paste a DM or comment. Agent 2 says how serious it is and what to ask next.</div>
        <textarea className="form-control" rows={3} value={dm.message}
          placeholder="Hi, I need a hole in my drywall fixed before Friday, I'm in Lehi. How much?"
          onChange={(e) => setDm({ ...dm, message: e.target.value })} />
        <div className="mk-row">
          <label>Photos or videos sent <input type="number" min="0" className="form-control mk-num" value={dm.photos}
            onChange={(e) => setDm({ ...dm, photos: e.target.value })} /></label>
          <button className="btn btn-primary btn-sm" disabled={!dm.message.trim()} onClick={qualify}>Check</button>
        </div>
        {read && (
          <div className={`mk-read ${read.tier}`}>
            <div><b>{read.tier.toUpperCase()}</b> · score {read.score}{read.city ? ` · ${read.city}` : ''}{read.inArea === false ? ' (outside the named cities)' : ''}</div>
            {read.facts.length > 0 && <div><i>Facts:</i> {read.facts.join(' ')}</div>}
            {read.hypotheses.length > 0 && <div><i>Guesses, to confirm:</i> {read.hypotheses.join(' ')}</div>}
            {read.actions.length > 0 && <ul>{read.actions.map((a) => <li key={a}>{a}</li>)}</ul>}
          </div>
        )}
      </div>

      {/* ── Run a campaign ────────────────────────────────────────────── */}
      <div className="card mk-card">
        <div className="mk-h">New test campaign</div>
        <div className="mk-sub">Utah County and Salt Lake County, Facebook groups. Judged by qualified leads and profit, never likes.</div>
        <div className="mk-chips">
          {SERVICES.map((s) => (
            <label key={s} className={`mk-chip${services.includes(s) ? ' on' : ''}`}>
              <input type="checkbox" checked={services.includes(s)}
                onChange={() => setServices(services.includes(s) ? services.filter((x) => x !== s) : [...services, s])} />
              {s}
            </label>
          ))}
        </div>
        {status?.modelConfigured && (
          <label className="mk-row"><input type="checkbox" checked={useModel} onChange={(e) => setUseModel(e.target.checked)} /> Let Claude write the posts (still checked by the rules)</label>
        )}
        <button className="btn btn-primary" disabled={busy || !services.length} onClick={run}>{busy ? 'Running the agents…' : 'Run test campaign'}</button>

        {list.length > 0 && (
          <div className="mk-list">
            {list.map((x) => (
              <button key={x._id} className={`mk-listrow${c?._id === x._id ? ' on' : ''}`} onClick={() => openCampaign(x._id)}>
                <span>{x.name}</span><span>{x.approved}/{x.variants} approved · {x.posted}/{x.placements} posted</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {c && (
        <>
          {/* ── Scoreboard ─────────────────────────────────────────────── */}
          <div className="ft-tally">
            <div className="ft-stat"><b>{r.totals.posted || 0}</b><span>Posted</span></div>
            <div className="ft-stat"><b>{r.totals.leads || 0}</b><span>Leads</span></div>
            <div className="ft-stat"><b>{r.totals.qualified || 0}</b><span>Qualified</span></div>
            <div className="ft-stat"><b>{r.totals.customers || 0}</b><span>Customers</span></div>
            <div className="ft-stat"><b>{money(r.totals.revenue)}</b><span>Revenue</span></div>
            <div className="ft-stat"><b>{money(r.totals.profit)}</b><span>Profit</span></div>
          </div>
          {(r.director.nextTests || []).length > 0 && (
            <div className="ft-brief"><div className="ft-brief-head">Director: test next</div>
              {r.director.nextTests.map((t) => <div key={t} className="ft-brief-item">{t}</div>)}
            </div>
          )}

          {/* ── Posts ──────────────────────────────────────────────────── */}
          <div className="mk-h mk-sec">Posts</div>
          {c.variants.map((v) => (
            <div key={v.id} className={`card mk-card mk-var ${v.status}`}>
              <div className="mk-row mk-between">
                <span className="mk-tag">{v.service} · {v.city}</span>
                <span className={`mk-status ${v.status}`}>{v.status.replace('_', ' ')}</span>
              </div>
              <p className="mk-text">{v.text}</p>
              {v.status !== 'approved' && v.reviewNote && <div className="mk-note">{v.reviewNote}</div>}
              {verdicts[v.id] && verdicts[v.id].verdict !== 'keep_testing' && (
                <div className="mk-note"><b>{VERDICT[verdicts[v.id].verdict]}</b>: {verdicts[v.id].reason}</div>
              )}
              <div className="mk-row">
                {v.status === 'approved' && <button className="btn btn-outline btn-sm" onClick={() => copy(v.text)}>Copy</button>}
                {(v.status === 'draft' || v.status === 'needs_owner') && (
                  <button className="btn btn-outline btn-sm" onClick={() => approve(v)}>{v.status === 'needs_owner' ? 'Owner approve' : 'Approve'}</button>
                )}
              </div>
            </div>
          ))}

          {/* ── Schedule and tracking ──────────────────────────────────── */}
          <div className="mk-h mk-sec">Schedule</div>
          <div className="mk-sub">{c.distribution.note}</div>
          <ul className="mk-rules">{(c.distribution.rules || []).map((x) => <li key={x}>{x}</li>)}</ul>
          {c.distribution.placements.map((p) => {
            const got = byPlacement[p.id] || p;
            const variant = c.variants.find((v) => v.id === p.variantId);
            return (
              <div key={p.id} className={`card mk-card mk-pl ${p.status}`}>
                <div className="mk-row mk-between">
                  <b>{p.date} · {p.time}</b>
                  <select className="form-control mk-sel" value={val(p, 'status')} onChange={(e) => edit(p, 'status', e.target.value)}>
                    <option value="planned">Planned</option><option value="posted">Posted</option>
                    <option value="skipped">Skipped</option><option value="rejected">Rejected</option>
                  </select>
                </div>
                <div className="mk-tag">{p.city} · {p.service}</div>
                <input className="form-control" placeholder={p.groupTarget} value={val(p, 'groupName')}
                  onChange={(e) => edit(p, 'groupName', e.target.value)} />
                {variant && <div className="mk-row"><button className="btn btn-outline btn-sm" onClick={() => copy(variant.text)}>Copy post</button>
                  <button className="btn btn-outline btn-sm" onClick={() => rejected(p)}>It got rejected</button></div>}
                <div className="mk-grid">
                  {COUNTS.map(([f, label]) => (
                    <label key={f}>{label}
                      <input type="number" min="0" className="form-control mk-num" value={val(p, f)}
                        onChange={(e) => edit(p, f, e.target.value)} />
                    </label>
                  ))}
                </div>
                <div className="mk-sub">From the CRM: {got.leads} lead(s), {got.qualified} qualified, {got.customers} customer(s), {money(got.profit)} profit.</div>
                <div className="mk-row">
                  <select className="form-control mk-sel" defaultValue="" onChange={(e) => { link(p, e.target.value); e.target.value = ''; }}>
                    <option value="">Link a service request to this post…</option>
                    {(requests || []).slice(0, 100).map((q) => (
                      <option key={q._id} value={q._id}>{q.fullName || 'Request'} · {q.city || ''} · {q.serviceType || ''}</option>
                    ))}
                  </select>
                  {drafts[p.id] && <button className="btn btn-primary btn-sm" onClick={() => savePlacement(p)}>Save</button>}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
